import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';

/**
 * A reusable copy-to-clipboard button component.
 * Displays a button with a copy icon that copies the provided text to clipboard.
 *
 * Sized to the value it follows, not to a toolbar. A `mat-icon-button` is 40px
 * square by default — beside a five-character app id that is a button three
 * times the height of its own value, and the empty half of it reads as a gap
 * between the number and the icon. At 24px the icon sits where the eye expects
 * the value to end. The touch target Material renders underneath is untouched,
 * so the hit area stays comfortable on a phone.
 */
@Component({
  selector: 'app-copy-button',
  template: `
    <button
      mat-icon-button
      [matTooltip]="tooltip"
      (click)="copy()"
      [disabled]="!text"
    >
      <mat-icon>{{ isCopied ? 'check' : 'content_copy' }}</mat-icon>
    </button>
  `,
  styleUrls: ['./copy-button.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, CommonModule],
})
export class CopyButtonComponent {
  @Input() text: string = '';
  @Input() tooltip: string = 'Copy to clipboard';

  isCopied = false;

  constructor(private clipboard: Clipboard) {}

  copy(): void {
    if (this.text) {
      this.clipboard.copy(this.text);
      this.isCopied = true;
      setTimeout(() => {
        this.isCopied = false;
      }, 2000);
    }
  }
}
